import React, { Fragment, useState } from 'react';
import { Button }from '@cmsgov/design-system';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

// Components

import InstructionalText from '../notifications/InstructionalText';
import Form from '@lib/wrappers/Form';
import ModalHeader from '@common-components/headers/ModalHeader';
import UniversalInput from '@lib/wrappers/UniversalInput';

// ActionCreators
import { removeException } from '@actions/ssap/ssapActionCreators';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { omit } from '@utils/miscellaneous/objectHelpers';
import { SSAP_INCOME_PROP_NAMES } from '@constants/ssap/miscellaneous/miscellaneous';

// Constants

import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { INSTRUCTIONAL_TEXT_STYLE_TYPES } from '@constants/common/styleTypes';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { DEDUCTIONS_SOURCES, INCOME_SOURCES } from '@constants/ssap/hhmTypes/incomeSourcesList';
import {
    ADDITIONAL_INPUT_EXCLUDES,
    EXPENSE_TYPES, INCOME_TYPES, INCOMES_WITH_INPUT_INSTRUCTIONS, OTHER_INCOME_TYPES, PARAMETER_TYPES
} from '@constants/ssap/hhmTypes/incomeAndExpenceTypes';
import { FREQUENCY_TYPES } from '@constants/ssap/hhmTypes/frequencyTypes';
import { getFrequencyOptions } from '@constants/ssap/hhmTypes/frequencyOptionsConfig';
import { FREQUENCY_GROUP, resolveFrequencyGroupForSource } from '@constants/ssap/hhmTypes/frequencyGroups';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import states from '@constants/configs/states';

export const getNegativeIncomeInstructionVisibility = (incomeType, isMN) => INCOMES_WITH_INPUT_INSTRUCTIONS.includes(incomeType) && isMN;

export const shouldShowInstructionalText = (parameter, localStateType, stateCode) => {
    if (!localStateType) return false;
    if (parameter === PARAMETER_TYPES.INCOMES){
        return localStateType !== INCOME_TYPES.OTHER;
    }
    if (parameter === PARAMETER_TYPES.EXPENSES) {
        if (stateCode === states.NEW_JERSEY) {
            return true;
        }
        return localStateType === EXPENSE_TYPES.ALIMONY
            || localStateType === EXPENSE_TYPES.STUDENT_LOAN_INTEREST;
    }
    return false;
};

export const getInstructionalTextConfig = (parameter, localStateType) => {
    if (!!localStateType && parameter === PARAMETER_TYPES.INCOMES) {
        return {
            instructionParams: [localStateType],
            onlyInstructions: localStateType === INCOME_TYPES.ALIMONY,
            fadeEffectDisabled: localStateType === INCOME_TYPES.ALIMONY
        };
    }

    if (!!localStateType && parameter === PARAMETER_TYPES.EXPENSES) {
        return {
            instructionParams: [localStateType, PARAMETER_TYPES.DEDUCTIONS],
            onlyInstructions: true,
            fadeEffectDisabled: true
        };
    }

    return null;
};

const AddIncomeOrDeductionSourceModal = props => {

    const {
        JOB,
        OTHER,
        UNSPECIFIED
    } = INCOME_TYPES;

    const {
        OTHER_INCOME
    } = OTHER_INCOME_TYPES;

    const {
        DAILY,
        HOURLY,
        ONCE,
        YEARLY
    } = FREQUENCY_TYPES;

    const {
        MODAL: MODAL_STYLE_TYPE
    } = INSTRUCTIONAL_TEXT_STYLE_TYPES;

    let {
        actions: {
            changeData,
            hideModal
        },
        bottomAnchors: [
            anchor_1,
            anchor_2,
        ],
        boundAnchor,
        data,
        data: {
            coverageYear
        },
        environment: {
            exchangeName,
            stateCode,
            isState,
            localDates: { serverDate }
        },
        form,
        formName,
        hyperReview,
        methods,
        modal: {
            modalData: {
                source: {
                    amount = null,
                    frequency = null,
                    sourceName = null,
                    type = null,
                    subType = UNSPECIFIED,
                    relatedExpense = null,
                    cyclesPerFrequency = null,
                    tribalAmount = null,
                    incomeExpectedForWholeYear = null,
                    incomeStartDate = null,
                    incomeEndDate = null
                },
                index,
                parameter,
                pageToReturn,
                option = undefined
            }
        },
        modalHeaderProps,
        modalHeaderProps: {
            modalId
        }
    } = props;


    const getDataInstructions = (t, source = 'source') => {
        return `instructional_text.${ source }.${ typeof t === 'string' ? t.toLowerCase() : 'null' }`;
    };

    const {
        hhmIndex: member,
        currentHhm: currentHouseholdMember
    } = props;

    const {
        name
    } = currentHouseholdMember;

    const [ localState, setLocalState ] = useState({
        amount,
        frequency,
        sourceName,
        type: option ? option : type,
        profit: amount > 0,
        subType,
        relatedExpense,
        cyclesPerFrequency,
        tribalAmount,
        incomeExpectedForWholeYear:
            (frequency === YEARLY || frequency === ONCE) ? true : incomeExpectedForWholeYear,
        incomeStartDate,
        incomeEndDate
    });

    const [ areChangesApplied, changeStatus ] = useState(false);

    const dispatch = useDispatch();

    const handleSaveClick = () => {

        // No-op Save: when the user opened the modal but didn't actually edit
        // anything, just close it. Falling through to the validate+save chain
        // would still dispatch `changeData` and an unnecessary /save round-trip.
        if(!areChangesApplied && form[formName].isEligiblyForSave) {
            hideModal();
            return;
        }

        // Build the post-save snapshot
        let newState;

        // HIX-204180: An incomes Edit that only changed `sourceName` must
        // NOT bump `maxAchievedPage` or trigger income recalculation
        let isOnlySourceNameChanged = false;

        const updateData = () => {
            newState = {
                ...data,
                ...(hyperReview ? { maxAchievedPage: pageToReturn } : {})
            };

            const rawForSave = omit(localState, ['profit']);
            const dataForSave = (rawForSave.frequency === YEARLY || rawForSave.frequency === ONCE)
                ? { ...rawForSave, incomeExpectedForWholeYear: true }
                : rawForSave;

            if(index === null) {
                newState
                    .taxHousehold
                    .householdMember[member][parameter]
                    .push(dataForSave);
            } else {
                const { sourceName } = dataForSave;
                const applicantGuid = newState.taxHousehold.householdMember[member].applicantGuid;
                const oldSourceName = newState.taxHousehold.householdMember[member][parameter][index].sourceName;

                if (parameter === PARAMETER_TYPES.INCOMES) {
                    const oldRow = data.taxHousehold.householdMember[member][parameter][index];
                    const changedKeys = Object.keys(dataForSave).filter(key => {
                        if (key === 'id') return false;
                        const next = dataForSave[key];
                        const prev = oldRow[key];
                        if (next == null && prev == null) return false;
                        return next !== prev;
                    });

                    isOnlySourceNameChanged = changedKeys.length === 1
                        && changedKeys[0] === SSAP_INCOME_PROP_NAMES.SOURCE_NAME;
                }

                newState.taxHousehold.householdMember[member][parameter][index] = {
                    ...newState.taxHousehold.householdMember[member][parameter][index],
                    ...dataForSave
                };

                if(sourceName !== oldSourceName) {
                    newState
                        .taxHousehold
                        .householdMember = newState
                            .taxHousehold
                            .householdMember.map(hhm => {
                                return {
                                    ...hhm,
                                    employerSponsoredCoverage: hhm.employerSponsoredCoverage
                                        .reduce((coverages, coverage) => {
                                            if(coverage.applicantGuid === applicantGuid && coverage.companyName === oldSourceName) {
                                                return [ ...coverages, { ...coverage, companyName: sourceName } ];
                                            } else {
                                                return [ ...coverages, coverage ];
                                            }
                                        }, []),
                                    healthReimbursementArrangement: hhm.healthReimbursementArrangement
                                        .reduce((hras, hra) => {
                                            if(hra.applicantGuid === applicantGuid && hra.employerName === oldSourceName) {
                                                return [ ...hras, { ...hra, employerName: sourceName } ];
                                            } else {
                                                return [ ...hras, hra ];
                                            }
                                        }, []),
                                };
                            });
                }
            }

            changeData(newState);
        };

        // Explicit /save dispatch through the page form.
        // Always clear EXCEPTIONS.EDITING_INCOMES_AND_DEDUCTIONS on save success
        const saveCallbacks = [hideModal, () => dispatch(removeException())];

        const saveData = () => {
            methods[formName].onFormSubmit(
                pageToReturn,
                saveCallbacks,
                {
                    skipFormEligibility: true,
                    formDataOverride: newState,
                    ...(
                        isOnlySourceNameChanged
                            ? {}
                            : { // restrict navigation and recalculate income if other than source name changed
                                maxAchievedPageOverride: pageToReturn,
                                recalculateIncome: true,
                            }
                    ),
                },
            );
        };

        // Validate the modal's inputs first. 
        // On success, run the callbacks synchronously:
        //   1. updateData - updates Redux via `changeData`.
        //   2. saveData - dispatches /save with `hideModal`
        methods[formName].onFormSubmitForValidationOnly([
            updateData,
            saveData,
        ]);
    };

    const onFieldChange = newData => {

        const [ key, value ] = Object.entries(newData)[0];

        if(key === 'type' && value !== localState.type) {
            setLocalState({
                amount: null,
                frequency: null,
                sourceName: null,
                type: newData.type,
                profit: null,
                subType: value === OTHER ? localState.subType : UNSPECIFIED,
                relatedExpense: null,
                tribalAmount: null,
                incomeExpectedForWholeYear: null,
                incomeStartDate: null,
                incomeEndDate: null
            });
        } else if(key === 'subType' && value !== localState.subType) {
            setLocalState({
                amount: null,
                frequency: null,
                sourceName: null,
                type: localState.type,
                profit: null,
                subType: newData.subType,
                relatedExpense: null,
                tribalAmount: null,
                incomeExpectedForWholeYear: null,
                incomeStartDate: null,
                incomeEndDate: null
            });
        } else if (key === 'amount') {
            setLocalState(state => ({
                ...{
                    ...state,
                    profit: value > 0
                },
                ...newData
            }));
        } else if (key === 'profit') {
            setLocalState(state => {
                return {
                    ...{
                        ...state,
                        amount: value === null ? state.amount : (value ? 1 : -1) * Math.abs(state.amount)
                    },
                    ...newData
                };
            });
        } else if (key === 'cyclesPerFrequency') {
            setLocalState(state => {
                return {
                    ...state,
                    cyclesPerFrequency: Number(value)
                };
            });
        } else if (key === 'frequency') {
            setLocalState(state => {
                const isYearlyOrOnce = value === YEARLY || value === ONCE;
                const wasYearlyOrOnce = state.frequency === YEARLY || state.frequency === ONCE;
                let nextIncomeExpectedForWholeYear = state.incomeExpectedForWholeYear;
                if (isYearlyOrOnce) {
                    nextIncomeExpectedForWholeYear = true;
                } else if (wasYearlyOrOnce) {
                    nextIncomeExpectedForWholeYear = null;
                }
                return {
                    ...state,
                    frequency: value,
                    cyclesPerFrequency: null,
                    incomeStartDate: isYearlyOrOnce ? null : state.incomeStartDate,
                    incomeEndDate: isYearlyOrOnce ? null : state.incomeEndDate,
                    incomeExpectedForWholeYear: nextIncomeExpectedForWholeYear
                };
            });
        } else if (key === 'incomeExpectedForWholeYear' && value === true) {
            setLocalState(state => {
                return {
                    ...state,
                    incomeExpectedForWholeYear: value,
                    incomeStartDate: null,
                    incomeEndDate: null
                };
            });
        } else {
            setLocalState(state => ({
                ...state,
                ...newData
            }));
        }
        areChangesApplied === false && changeStatus(true);
    };

    const getAdditionalInputByType = inputsData => {
        switch(inputsData.additionalInput.inputType) {
            case INPUT_TYPES.DROPDOWN:
                return getDropdownInput(inputsData);
            case INPUT_TYPES.MONEY_INPUT:
                return getMoneyInput(inputsData);
            case INPUT_TYPES.REGULAR_INPUT:
                return getRegularInput(inputsData);
            default:
                return <p>{'Input\'s been lost'}</p>;
        }
    };

    const getRegularInput = inputsData => {
        const {
            additionalInput: {
                additionalInputText,
                required,
                validationType
            }
        } = inputsData;

        const propName = 'sourceName';

        // HIX-204180: Marking sourceName change as standalone change
        return (
            <UniversalInput
                label={{
                    text: additionalInputText
                }}
                inputData={{
                    isInputRequired: required,
                    isInputStandalone: true,
                    type: INPUT_TYPES.REGULAR_INPUT,
                    name: propName,
                    value: localState[propName],
                    placeholder: additionalInputText.replace('label', 'placeholder'),
                    validationType,
                    currentData: { [propName]: localState[propName] },
                    fields: []
                }}
                inputActions={{
                    onFieldChange
                }}
            />
        );
    };

    const getMoneyInput = inputsData => {
        const {
            additionalInput: {
                additionalInputText,
                required,
                isOnlyPositiveAvailable,
            }
        } = inputsData;


        return (
            <UniversalInput
                label={{
                    text: additionalInputText,
                    labelClasses: ['tablet:grid-col-4']
                }}
                inputData={{
                    isInputRequired: required,
                    type: INPUT_TYPES.MONEY_INPUT,
                    name: 'relatedExpense',
                    value: localState.relatedExpense,
                    inputClasses: ['tablet:grid-col-6'],
                    valuesToMatch: localState.amount >= 0 ? [1, localState.amount] : [],
                    isOnlyPositiveAvailable: !!isOnlyPositiveAvailable,
                    placeholder: additionalInputText.replace('label', 'placeholder'),
                    validationType: VALIDATION_TYPES.MONEY_NOT_EXCEED,
                    validationErrorMessage: localState.relatedExpense > 0 ? VALIDATION_ERROR_MESSAGES.EDUCATION_EXPENSES : VALIDATION_ERROR_MESSAGES.MONEY,
                    currentData: { relatedExpense: localState.relatedExpense },
                    fields: []
                }}
                inputActions={{
                    onFieldChange
                }}
            />
        );
    };

    const getDropdownInput = inputsData => {
        const {
            additionalInput: {
                additionalInputText,
                required
            }
        } = inputsData;


        const propName = localState.type === OTHER ? 'subType' : 'profit';
        const options = localState.type === OTHER ? (DROPDOWN_OPTIONS.OTHER_INCOME_SUBTYPES(isState)) : DROPDOWN_OPTIONS.PROFIT_LOSS;
        const validationType = localState.type === OTHER ? VALIDATION_TYPES.NOT_UNSPECIFIED : VALIDATION_TYPES.CHOSEN;
        
        return (
            <Fragment>
                <UniversalInput
                    label={{
                        text: additionalInputText
                    }}
                    inputData={{
                        isInputRequired: required,
                        type: INPUT_TYPES.DROPDOWN,
                        name: propName,
                        value: localState[propName],
                        returnAsBoolean: true,
                        placeholder: additionalInputText.replace('label', 'placeholder'),
                        options: options,
                        validationType: validationType,
                        currentData: { [propName]: localState[propName] },
                        fields: []
                    }}
                    inputActions={{
                        onFieldChange
                    }}
                />
                {
                    localState.subType !== UNSPECIFIED &&

                        <InstructionalText
                            Component = {
                                <p 
                                    className = 'usa-grid' 
                                    data-instructions = { getDataInstructions(localState.subType) } 
                                    
                                />
                            }
                            styleType = { MODAL_STYLE_TYPE }
                        />
                }
            </Fragment>
        );
    };

    const getRestOfInputs = () => {
        let inputsData = parameter === 'incomes' ?
            INCOME_SOURCES.find(source => source.type === localState.type) :
            DEDUCTIONS_SOURCES.find(source => source.type === localState.type);
        // Override notice for MN specific income types
        if (inputsData && isState.MN && INCOMES_WITH_INPUT_INSTRUCTIONS.includes(localState.type)) {
            inputsData = {
                ...inputsData,
                notice: {
                    text: 'modals.income_deduction.negative_income_question',
                    positionOnPage: 2
                }
            };
        }
        const mockInputsData = {
            isOnlyPositiveAvailable: false,
            additionalInput: {
                positionOnPage: null,
            },
            notice: {
                positionOnPage: null,
                text: ''
            },
            questionText: '',
            frequencyGroup: FREQUENCY_GROUP.NONE
        };

        const {
            isOnlyPositiveAvailable,
            additionalInput: {
                positionOnPage: inputPosition
            },
            notice: {
                positionOnPage: noticePosition,
                text: noticeText
            },
            questionText,
        } = inputsData ? inputsData : mockInputsData;

        const frequencyGroup = resolveFrequencyGroupForSource(inputsData, localState.subType);

        const shouldShowNegativeIncomeInstructions = getNegativeIncomeInstructionVisibility(localState.type, isState.MN);

        const getEndDateValidationConfig = () => {
            if (!isState?.MN || parameter !== 'incomes') {
                //only validate greater from start date (if provided) for non-MN states
                return {
                    valuesToMatch: localState.incomeStartDate,
                    specificValidation: {
                        validationType: VALIDATION_TYPES.DATE_OR_NULL_GREATER_THAN_START
                    },
                };
            } else {
                const currentDate = new Date(serverDate);
                const currentMonthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
                // Case 1: Start date is provided
                if (localState.incomeStartDate) {
                    return {
                        valuesToMatch: [localState.incomeStartDate, currentMonthEnd],
                        specificValidation: {
                            validationType: VALIDATION_TYPES.DATE_BETWEEN_TWO_DATES
                        },
                        validationErrorMessage: new Date(localState.incomeEndDate) > currentMonthEnd
                            ? 'ssap.validation_error_message.date_not_within_current_month'
                            : 'ssap.validation_error_message.invalid_date_or_not_after_start'
                    };
                };
                // Case 2: Start date is null - only validate against current month end
                return {
                    valuesToMatch: currentMonthEnd,
                    specificValidation: {
                        validationType: VALIDATION_TYPES.DATE_NOT_GREATER_THAN_CURRENT
                    },
                    validationErrorMessage: 'ssap.validation_error_message.date_not_within_current_month'
                };
            }
        };

        return inputsData ?
            <Fragment>
                {
                    noticePosition === 0 &&
                    <div className='grid-container'>
                        <p className="grid-row controls">
                            <FormattedMessage
                                id={noticeText}
                                values={{
                                    personName: normalizeNameOfPerson(name),
                                    coverageYear
                                }}
                                tagName={isState.MN ? '' : 'strong'}
                            />
                        </p>
                    </div>
                }

                {
                    inputPosition === 1 &&
                    getAdditionalInputByType(inputsData)
                }

                {
                    noticePosition === 1 && localState.subType === OTHER_INCOME &&
                    <div className='grid-container'>
                        <p className="grid-row controls">
                            <FormattedMessage
                                id={noticeText}
                                values={{
                                    personName: normalizeNameOfPerson(name),
                                    coverageYear
                                }}
                                tagName={isState.MN ? '' : 'strong'}
                            />
                        </p>
                    </div>
                }

                <div className='grid-container'>
                    <p className="grid-row controls">
                        <FormattedMessage
                            id={questionText}
                            values={{
                                personName: normalizeNameOfPerson(name),
                                coverageYear,
                                br: <br/>
                            }}
                            tagName={isState.MN ? '' : 'strong'}
                        />
                    </p>
                </div>
                {
                    noticePosition === 2 &&
                    <div className='grid-container'>
                        {
                            shouldShowNegativeIncomeInstructions ?
                                <>
                                    <InstructionalText
                                        Component={
                                            isState.MN ? (
                                                <span
                                                    className="required-false"
                                                    data-instructions="instructional_text.negative_income_instructions"
                                                >
                                                    {noticeText}
                                                </span>
                                            ) : (
                                                <strong
                                                    className="required-false"
                                                    data-instructions="instructional_text.negative_income_instructions"
                                                >
                                                    {noticeText}
                                                </strong>
                                            )
                                        }
                                    />
                                </>
                                :
                                <p className="grid-row controls">
                                    <FormattedMessage
                                        id={noticeText}
                                        values={{
                                            personName: normalizeNameOfPerson(name),
                                            coverageYear
                                        }}
                                        tagName='strong'
                                    />
                                </p>
                        }
                    </div>
                }

                <UniversalInput
                    label={{
                        text: 'ssap.input.label.amount',
                        labelClasses: ['tablet:grid-col-4']
                    }}
                    inputData={{
                        isInputRequired: true,
                        type: INPUT_TYPES.MONEY_INPUT,
                        name: 'amount',
                        value: localState.amount,
                        inputClasses: ['tablet:grid-col-6'],
                        isOnlyPositiveAvailable: (ADDITIONAL_INPUT_EXCLUDES.includes(localState.type) && isState.MN) ? true : !!isOnlyPositiveAvailable,
                        placeholder: 'ssap.input.placeholder.amount',
                        validationType: VALIDATION_TYPES.MONEY,
                        currentData: { amount: localState.amount },
                        fields: []
                    }}
                    inputActions={{
                        onFieldChange
                    }}
                />
                {
                    parameter === PARAMETER_TYPES.EXPENSES &&
                    localState.type === EXPENSE_TYPES.STUDENT_LOAN_INTEREST &&
                    <InstructionalText
                        Component={
                            <p data-instructions='modals.income_deduction.student_loan_interest.amount_help' />
                        }
                        styleType={MODAL_STYLE_TYPE}
                        onlyInstructions={true}
                        fadeEffectDisabled={true}
                    />
                }
                {
                    inputPosition === 2 &&
                    !(ADDITIONAL_INPUT_EXCLUDES.includes(localState.type) && isState.MN) &&
                    getAdditionalInputByType(inputsData)
                }

                <UniversalInput
                    label={{
                        text: 'ssap.input.label.how_often',
                        isLabelRequired: false
                    }}
                    inputData={{
                        isInputRequired: true,
                        type: INPUT_TYPES.DROPDOWN,
                        name: 'frequency',
                        value: localState.frequency,
                        options: getFrequencyOptions(isState, localState.type, frequencyGroup, localState.subType, parameter),
                        validationType: VALIDATION_TYPES.CHOSEN,
                        currentData: { frequency: localState.frequency },
                        fields: []

                    }}
                    inputActions={{
                        onFieldChange
                    }}
                />

                {
                    (localState.frequency === HOURLY ||
                        localState.frequency === DAILY) &&
                        localState.type === JOB &&

                    <Fragment>
                        <p className="grid-row controls">
                            <FormattedMessage
                                id='modals.income_deduction.how_much_per_week'
                                values={{
                                    personName: normalizeNameOfPerson(name)
                                }}
                                tagName={isState.MN ? '' : 'strong'}
                            />
                        </p>
                        <UniversalInput
                            label={{
                                text: localState.frequency === HOURLY ?
                                    'ssap.input.label.hours_per_week' : 'ssap.input.label.days_per_week'
                            }}
                            inputData={{
                                isInputRequired: true,
                                type: INPUT_TYPES.REGULAR_INPUT,
                                name: 'cyclesPerFrequency',
                                value: localState.cyclesPerFrequency,
                                placeholder: 'ssap.input.placeholder.qty',
                                validationType: VALIDATION_TYPES.RANGE,
                                valuesToMatch: localState.frequency === HOURLY ? [1, 168] : [1, 7],
                                validationErrorMessage: localState.frequency === HOURLY ?
                                    VALIDATION_ERROR_MESSAGES.RANGE_UP_TO_168 :
                                    VALIDATION_ERROR_MESSAGES.RANGE_UP_TO_7,
                                currentData: { cyclesPerFrequency: localState.cyclesPerFrequency },
                                fields: []
                            }}
                            inputActions={{
                                onFieldChange
                            }}
                        />
                    </Fragment>
                }

                {
                    noticePosition === 3 &&
                    <div className='grid-container'>
                        <p className="grid-row controls">
                            <FormattedMessage
                                id={noticeText}
                                values={{
                                    personName: normalizeNameOfPerson(name),
                                    coverageYear
                                }}
                            />
                        </p>
                    </div>
                }

                {
                    inputPosition === 3 &&
                    getAdditionalInputByType(inputsData)
                }
                { (localState.frequency !== null && localState.frequency !== YEARLY && localState.frequency !== ONCE) && (
                    <Fragment>
                        <UniversalInput
                            legend={{
                                legendText: 'ssap.input.legend.income_expected_for_whole_year',
                                legendTextReplacements: {
                                    source: parameter === PARAMETER_TYPES.INCOMES ?
                                        <FormattedMessage id='ssap.input.legend.income_expected_for_whole_year.earn_this_income' /> :
                                        <FormattedMessage id='ssap.input.legend.income_expected_for_whole_year.apply_this_deduction' />,
                                    personName: normalizeNameOfPerson(name)
                                }
                            }}
                            label={{
                                labelClass: ['tablet:grid-col-4 gi-radio_buttons gi-grid__layout-inline gi-col-layout__legend padding-r-1'],
                                ulClass: ['tablet:grid-col-6 gi-grid__layout-inline'],
                                showRequiredIcon: true
                            }}
                            inputData={{
                                isInputRequired: true,
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                name: 'incomeExpectedForWholeYear',
                                value: localState.incomeExpectedForWholeYear,
                                options: DROPDOWN_OPTIONS.YES_NO,
                                validationType: VALIDATION_TYPES.CHOSEN,
                                currentData: { incomeExpectedForWholeYear: localState.incomeExpectedForWholeYear },
                                fields: []
                            }}
                            inputActions={{
                                onFieldChange
                            }}
                        />
                        {
                            localState.frequency !== YEARLY &&
                                localState.frequency !== ONCE &&
                                localState.incomeExpectedForWholeYear === false &&

                                <Fragment>
                                    <UniversalInput
                                        label={{
                                            labelClass: ['tablet:grid-col-4 gi-grid__layout-inline gi-col-layout__label'],
                                            text: 'ssap.input.label.income_start_date',
                                            labelTextReplacements: { type: parameter },
                                        }}
                                        inputData={{
                                            isInputRequired: false,
                                            type: INPUT_TYPES.DATE_PICKER,
                                            inputClasses: ['tablet:grid-col-6'],
                                            name: 'incomeStartDate',
                                            value: localState.incomeStartDate,
                                            specificValidation: {
                                                validationType: VALIDATION_TYPES.DATE_OR_NULL
                                            },
                                            currentData: { incomeStartDate: localState.incomeStartDate },
                                            fields: []
                                        }}
                                        inputActions={{
                                            onFieldChange
                                        }}
                                    />

                                    <UniversalInput
                                        label={{
                                            labelClass: ['tablet:grid-col-4 gi-grid__layout-inline gi-col-layout__label'],
                                            text: 'ssap.input.label.income_end_date',
                                            labelTextReplacements: { type: parameter },
                                        }}
                                        inputData={{
                                            isInputRequired: false,
                                            type: INPUT_TYPES.DATE_PICKER,
                                            inputClasses: ['tablet:grid-col-6'],
                                            name: 'incomeEndDate',
                                            value: localState.incomeEndDate,
                                            ...getEndDateValidationConfig(),
                                            currentData: { incomeEndDate: localState.incomeEndDate },
                                            fields: []
                                        }}
                                        inputActions={{
                                            onFieldChange
                                        }}
                                    />

                                    {
                                        !isState?.NJ &&

                                            <InstructionalText
                                                Component = {
                                                    <p data-instructions = 'instructional_text.income_proration_estimate' />
                                                }
                                                styleType = { MODAL_STYLE_TYPE }
                                                replacements = {{ exchangeName }}
                                            />
                                    }
                                </Fragment>
                        }
                    </Fragment>
                )}

            </Fragment> :
            null;
    };

    const getOptions = (parameter, option) => {
        const INCOME_SOURCE_OPTIONS = DROPDOWN_OPTIONS.INCOME_SOURCES(isState);
        const DEDUCTION_SOURCE_OPTIONS = DROPDOWN_OPTIONS.DEDUCTION_SOURCES_FOR_STATE(stateCode);

        switch(parameter) {
            case 'incomes':
                return option
                    ? DROPDOWN_OPTIONS.INCOME_SOURCES_TRIBAL_INCOME
                    : index !== null
                        ? [INCOME_SOURCE_OPTIONS.find(option => option.value === type)]
                        : INCOME_SOURCE_OPTIONS;
            case 'expenses':
                return index !== null
                    ? [DEDUCTION_SOURCE_OPTIONS.find(option => option.value === type)]
                    : DEDUCTION_SOURCE_OPTIONS;
        }
    };

    const handleHideClick = () => hideModal();
    const createInstructionalText = () => {
        const config = getInstructionalTextConfig(parameter, localState.type);
        if (!config) return null;
        const instructionId = `${config?.instructionParams[1]?.toLowerCase()}_${config?.instructionParams[0]?.toLowerCase()}`;
        return (
            <InstructionalText
                Component={
                    <p
                        className='grid-row'
                        data-instructions={getDataInstructions(...config.instructionParams)}
                    />
                }
                replacements={{ br: <br/> }}
                skipParsing={true}
                onlyInstructions={config.onlyInstructions}
                fadeEffectDisabled={config.fadeEffectDisabled}
                styleType={MODAL_STYLE_TYPE}
                instructionId={instructionId}
            />
        );
    };

    modalHeaderProps = {
        ...modalHeaderProps,
        modalHeaderTextData: [
            modalHeaderProps.modalHeaderTextData(parameter === 'incomes'),
            { personName: normalizeNameOfPerson(name) }
        ]
    };

    return (
        <Fragment>
            <div className="gi-modal__header__head">
                <ModalHeader { ...modalHeaderProps } />
            </div>
            <div className="gi-modal__body" id='addIncomeOrDeductionModal' data-testid={`${modalId}_body`}>
                <Form { ...{ formName } }>
                    <UniversalInput
                        label={{
                            text: 'ssap.input.label.what_type_of',
                            labelTextReplacements: { type: parameter },
                            isLabelRequired: false,
                        }}
                        inputData={{
                            isInModal: true,
                            isInputRequired: true,
                            type: INPUT_TYPES.REACT_SELECT,
                            name: 'type',
                            value: localState.type,
                            options: getOptions(parameter, option),
                            validationType: VALIDATION_TYPES.CHOSEN,
                            currentData: { type: localState.type },
                            fields: []

                        }}
                        inputActions={{
                            onFieldChange
                        }}
                    />

                    {shouldShowInstructionalText(parameter, localState.type, stateCode) && createInstructionalText()}

                    {
                        localState.type && getRestOfInputs()
                    }
                </Form>
            </div>
            <div className="gi-modal__footer">
                <div className='grid-row controls'>
                    <div className='grid-offset-4 txt-left'>
                        <Button
                            className='usa-button usa-button--outline gi-button gi-button__outline'
                            data-testid={`${modalId}-cancel_btn`}
                            onClick={handleHideClick}
                            inputRef={boundAnchor(anchor_2)}
                            id='anchor'
                        >
                            <FormattedMessage id='button.cancel'/>
                        </Button>
                        <Button
                            className='usa-button gi-button gi-button__primary'
                            data-testid={`${modalId}-save_btn`}
                            onClick={handleSaveClick}
                            inputRef={boundAnchor(localState.type ? anchor_1 : {})}
                            disabled={!localState.type}
                        >
                            <FormattedMessage id='button.save'/>
                        </Button>
                    </div>
                </div>
            </div>

        </Fragment>
    );
};

export default AddIncomeOrDeductionSourceModal;
